import { inject, Injectable, isDevMode } from '@angular/core';
import {
  Auth, createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword,
  signInWithPopup, User, user, UserCredential, AuthProvider, signOut,
  signInWithCustomToken,
  setPersistence, browserLocalPersistence,
  getAuth
} from '@angular/fire/auth';
import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { updateProfile } from 'firebase/auth';
import { environment } from 'src/environments/environment';
import { ApmService } from '@elastic/apm-rum-angular';
import { getApp } from '@angular/fire/app';
import { redirectUnauthorizedTo } from '@angular/fire/auth-guard';
import { NavController } from '@ionic/angular';

let isLoggedIn = false;

export const canActivate: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authService = getAuth(getApp('conversia'));
  const router = inject(Router);

  if (isLoggedIn) {
    return Promise.resolve(true);
  } else {
    return router.createUrlTree(['login']);
  }
};

export const canActivateAuth: CanActivateChildFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => canActivate(route, state);

export const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['login']);

export const canActivateLogin: CanActivateChildFn =
  (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {

    const router = inject(Router);

    if (!isLoggedIn) {
      return Promise.resolve(true);
    } else {
      return router.createUrlTree(['home']);
    }
  };

export const redirectUnauthorizedToHome = () => redirectUnauthorizedTo(['home']);

@Injectable({
  providedIn: 'root',
})

export class AuthService {

  //isLoggedIn = false;

  pendingEmail = '';
  pendingPwd = '';

  authUserSub: Subscription;
  userSub: Subscription;
  lastUserUid: string;

  private claimsSource = new BehaviorSubject<any>(null);
  claims$ = this.claimsSource.asObservable();

  user$ = user(this.auth);

  //private exAuth = getAuth(getApp('expressia'));

  private userAuthData: User; // Save logged in user data

  constructor(
    private router: Router,
    private apmService: ApmService,
    private auth: Auth,
    private navCtrl: NavController
  ) {
    //this.auth.setPersistence(browserLocalPersistence);

    this.authUserSub = this.user$.subscribe((aUser: User | null) => {
      //handle user state changes here. Note, that user will be null if there is no currently logged in user.
      if (aUser) {
        // if (this.userSub) {
        //   this.userSub.unsubscribe();
        // }
        this.userAuthData = aUser;
        this.lastUserUid = aUser.uid;
        this.fetchPrivileges();
        if (isDevMode()) {
          console.log('auth: logado', this.userAuthData);
        }
        this.apmService.apm.setUserContext({
          id: aUser.uid,
          username: aUser.displayName,
          email: aUser.email
        });
        isLoggedIn = true;
      } else {
        this.userAuthData = undefined;
        this.claimsSource.next(null);
        if (isDevMode()) {
          console.log('auth: logout', this.lastUserUid);
        }
        isLoggedIn = false;
        //this.exAuth.signOut();
      }
    });
  }

  // Returns true when user is logged
  get isLoggedIn(): boolean {
    return this.userAuthData !== undefined;
  }

  // Returns true when user is logged in and email is verified
  get isLoggedInVerified(): boolean {
    return (this.userAuthData !== undefined) && (this.userAuthData?.emailVerified);
  }

  // DOC: https://firebase.google.com/docs/auth/admin/custom-claims
  async fetchPrivileges() {
    if (this.isLoggedIn) {
      //await this.auth.currentUser.getIdToken();
      await this.auth.currentUser.getIdTokenResult(false).then((idTokenResult) => {
        // Confirm the user is an Admin.
        if (idTokenResult.claims) {
          this.claimsSource.next(idTokenResult.claims);
        }
      });
    }
  }

  ngOnDestroy() {
    // when manually subscribing to an observable remember to unsubscribe in ngOnDestroy
    this.authUserSub.unsubscribe();
    if (this.userSub) {
      this.userSub.unsubscribe();
    }
  }

  // Sign in with email/password
  async signIn(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password).then(() => {
      this.navCtrl.navigateForward('/home');
    });
  }

  // Sign up with email/password
  async signUp(email: string, password: string, displayName: string) {
    return createUserWithEmailAndPassword(this.auth, email, password)
      .then((userCredential) => {
        // send verification mail.
        //this.sendVerificationMail();
        // update display name
        updateProfile(userCredential.user, {
          displayName,
        });
        this.navCtrl.navigateForward('/home');

        return userCredential;
      });
  }

  // Send email verfificaiton when new user sign up
  // async sendVerificationMail() {

  //   const actionCodeSettings = {
  //     url: 'https://conversia.life/?email=' + this.userAuthData.email,
  //     // iOS: {
  //     //   bundleId: 'life.conversia',
  //     //   minimumVersion: '23'
  //     // },
  //     // android: {
  //     //   packageName: 'life.conversia',
  //     //   installApp: true,
  //     //   minimumVersion: '23'
  //     // },
  //     // handleCodeInApp: true,
  //     // When multiple custom dynamic link domains are defined, specify which
  //     // one to use.
  //     //dynamicLinkDomain: environment.dynamicLinkDomain
  //   };


  //   return sendEmailVerification(this.auth.currentUser, actionCodeSettings)
  //     .then(() => {
  //       this.router.navigate(['home']);
  //     });
  //   // return this.afAuth.currentUser
  //   //   .then((u: any) => u.sendEmailVerification())
  //   //   .then(() => {
  //   //     this.router.navigate(['verify-email']);
  //   //   });
  // }

  // Recover password
  async forgotPassword(passwordResetEmail: string) {
    return sendPasswordResetEmail(this.auth, passwordResetEmail);
  }

  // Sign out
  async signOut() {
    return signOut(this.auth);
  }

  // Sign in with provider
  async oAuthProvider(provider: AuthProvider) {
    return signInWithPopup(this.auth, provider);
  }

  get userAuth() {
    return this.userAuthData;
  }

  get claims() {
    return this.claimsSource.value;
  }

  isAdmin() {
    return this.claims?.admin > 0;
  }

  isSupport() {
    return this.claims?.support > 0 || this.isAdmin();
  }

  emailVerified() {
    return this.userAuthData?.emailVerified;
  }

  getIdToken() {
    return this.userAuthData?.getIdToken(false);
  }

  userName() {
    return this.userAuthData?.displayName;
  }

  
}
